// src/pages/platform-admin/NationalTopicSimilarityTrend.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Avatar,
    Chip,
    LinearProgress,
    useTheme,
    Tooltip,
    Alert,
    CircularProgress,
    Fade,
    Zoom,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Slider,
    useMediaQuery
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    TrendingUp,
    TrendingDown,
    TrendingFlat,
    CheckCircle,
    Warning,
    Info,
    Timeline,
    People,
    Article,
    Public,
    EmojiEvents,
    BarChart,
    ShowChart,
    Assessment,
    School,
    Download,
    Schedule,
    Error as ErrorIcon,
    PlayArrow,
    Science,
    Psychology,
    Lightbulb,
    Flag,
    Analytics
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APINationalTopicTrends,
    APINationalTopicSimilarity,
    APINationalTopicCountryDistribution,
    APINationalTopicTrendingKeywords,
    APINationalTopicRunAnalysis
} from '../../apiServices/APIs';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const TopicSimilarityAnalysis = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [trends, setTrends] = useState([]);
    const [similarities, setSimilarities] = useState([]);
    const [countryDistribution, setCountryDistribution] = useState([]);
    const [trendingKeywords, setTrendingKeywords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [similarityStats, setSimilarityStats] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedPeriod, setSelectedPeriod] = useState('quarterly');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [openAnalysisDialog, setOpenAnalysisDialog] = useState(false);
    const [analysisConfig, setAnalysisConfig] = useState({ threshold: 30, algorithm_version: 'v1.0' });
    const [runningAnalysis, setRunningAnalysis] = useState(false);
    const showToast = useCustomToast();
    const isMounted = useRef(true);

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchAllData();
        
        return () => {
            isMounted.current = false;
        };
    }, [selectedPeriod, selectedYear]);

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            await Promise.all([
                fetchTrends(),
                fetchSimilarityAnalysis(),
                fetchCountryDistribution(),
                fetchTrendingKeywords()
            ]);
        } catch (err) {
            setError("Failed to load data");
            console.error("Fetch error:", err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchTrends = async () => {
        const result = await apiService.list(APINationalTopicTrends, { period: selectedPeriod, year: selectedYear });
        if (result.success && result.data && isMounted.current) {
            setTrends(result.data.trends || []);
            setSummary(result.data.summary);
        }
    };

    const fetchSimilarityAnalysis = async () => {
        const result = await apiService.list(APINationalTopicSimilarity);
        if (result.success && result.data && isMounted.current) {
            setSimilarities(result.data.similarities || []);
            setSimilarityStats(result.data.stats);
        }
    };

    const fetchCountryDistribution = async () => {
        const result = await apiService.list(APINationalTopicCountryDistribution, { period: selectedPeriod, year: selectedYear });
        if (result.success && result.data && isMounted.current) {
            setCountryDistribution(result.data);
        }
    };

    const fetchTrendingKeywords = async () => {
        const result = await apiService.list(APINationalTopicTrendingKeywords, { period: selectedPeriod });
        if (result.success && result.data && isMounted.current) {
            setTrendingKeywords(result.data);
        }
    };

    const handleRunAnalysis = async () => {
        setRunningAnalysis(true);
        try {
            const formData = new FormData();
            formData.append('threshold', analysisConfig.threshold);
            formData.append('algorithm_version', analysisConfig.algorithm_version);
            
            const result = await apiService.update(APINationalTopicRunAnalysis, formData);
            
            if (result && result.success) {
                showToast("Success", result.message, "success");
                fetchSimilarityAnalysis();
                setOpenAnalysisDialog(false);
            }
        } catch (error) {
            showToast("Error", "Failed to run analysis", "error");
        } finally {
            setRunningAnalysis(false);
        }
    };

    const getTrendIcon = (status) => {
        switch(status) {
            case 'emerging': return <TrendingUp sx={{ color: '#2196f3' }} />;
            case 'growing': return <TrendingUp sx={{ color: '#4caf50' }} />;
            case 'peak': return <TrendingFlat sx={{ color: '#ff9800' }} />;
            case 'declining': return <TrendingDown sx={{ color: '#f44336' }} />;
            default: return <CheckCircle sx={{ color: '#9e9e9e' }} />;
        }
    };

    const getInnovationColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'info';
        if (score >= 40) return 'warning';
        return 'error';
    };

    if (loading) {
        return <LoadingState message="Loading Topic Trends..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchAllData} title="Unable to Load Topic Trends" variant="research" fullPage retryText="Retry Loading" />;
    }

    const years = [2024, 2023, 2022, 2021];

    return (
        <>
            
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <PlatformAdminBar  notificationCount={14} />
                {/* Header Section */}
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        flexWrap: 'wrap', 
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: theme.palette.primary.main, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            <Science /> National Topic Similarity & Trends
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                startIcon={<PlayArrow />} 
                                onClick={() => setOpenAnalysisDialog(true)}
                                size={isMobile ? "small" : "medium"}
                            >
                                Run Analysis
                            </Button>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                startIcon={<RefreshIcon />} 
                                onClick={fetchAllData} 
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Filters Row */}
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        mt: 2, 
                        flexWrap: 'wrap',
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                    }}>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                            <InputLabel>Period</InputLabel>
                            <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)} label="Period">
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="quarterly">Quarterly</MenuItem>
                                <MenuItem value="annual">Annual</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100 } }}>
                            <InputLabel>Year</InputLabel>
                            <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} label="Year">
                                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </Paper>

                {/* Summary Cards */}
                {summary && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.info.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.total_clusters}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Topic Clusters
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.success.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.emerging_trends}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Emerging Trends
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: theme.palette.warning.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' }
                                    }}>
                                        {summary.avg_innovation_score}%
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        Avg Innovation
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                textAlign: 'center', 
                                bgcolor: summary.high_risk_duplication > 0 ? theme.palette.error.light + '20' : theme.palette.success.light + '20',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' }
                            }}>
                                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Typography variant="h3" sx={{ 
                                        fontWeight: 'bold',
                                        fontSize: { xs: '1.5rem', sm: '2rem' },
                                        color: summary.high_risk_duplication > 0 ? theme.palette.error.main : 'inherit'
                                    }}>
                                        {summary.high_risk_duplication}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                        High Duplication Risk
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Box>
                {/* Tabs */}
                <Paper elevation={2} sx={{ 
                    borderRadius: 3, 
                    overflow: 'hidden',
                    backgroundColor: 'white',
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, v) => setActiveTab(v)}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider', 
                            px: { xs: 1, sm: 2 }, 
                            pt: 2,
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 },
                                px: { xs: 1, sm: 2 }
                            }
                        }}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                    >
                        <Tab label="Topic Clusters" />
                        <Tab label="Similarity Analysis" />
                        <Tab label="Country Distribution" />
                        <Tab label="Trending Keywords" />
                    </Tabs>

                    {/* Topic Clusters Tab */}
                    {activeTab === 0 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                            <TableCell><strong>Cluster Name</strong></TableCell>
                                            <TableCell align="center"><strong>Size</strong></TableCell>
                                            <TableCell align="center"><strong>Innovation</strong></TableCell>
                                            <TableCell align="center"><strong>Duplication Risk</strong></TableCell>
                                            <TableCell align="center"><strong>Status</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {trends.map((trend) => (
                                            <TableRow key={trend.id} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ 
                                                            fontWeight: 500,
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}>
                                                            {trend.topic_cluster}
                                                        </Typography>
                                                        {trend.keywords && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                                {trend.keywords.slice(0, 3).join(', ')}...
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={trend.cluster_size} 
                                                        size="small" 
                                                        color="primary"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={trend.innovation_level} 
                                                        size="small" 
                                                        color={getInnovationColor(trend.innovation_score)}
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={trend.duplication_risk_label} 
                                                        size="small" 
                                                        color={trend.duplication_risk >= 70 ? 'error' : trend.duplication_risk >= 50 ? 'warning' : 'info'}
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        icon={getTrendIcon(trend.trend_status)} 
                                                        label={trend.formatted_trend_status} 
                                                        size="small" 
                                                        color={trend.trend_status_color}
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {trends.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">
                                                    <Typography color="text.secondary" sx={{ py: 4 }}>
                                                        No trend data available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Similarity Analysis Tab */}
                    {activeTab === 1 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            {similarityStats && (
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                                <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                                    {similarityStats.total_pairs}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    Total Pairs
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ 
                                            bgcolor: theme.palette.error.light + '20',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'translateY(-4px)' }
                                        }}>
                                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                                <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                                    {similarityStats.high_similarity}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    High Similarity
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ 
                                            bgcolor: theme.palette.warning.light + '20',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'translateY(-4px)' }
                                        }}>
                                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                                <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                                    {similarityStats.medium_similarity}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    Medium Similarity
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Card sx={{ 
                                            bgcolor: theme.palette.success.light + '20',
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'translateY(-4px)' }
                                        }}>
                                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                                <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                                    {similarityStats.average_similarity}%
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    Avg Similarity
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table size="small" sx={{ minWidth: { xs: 700, sm: 'auto' } }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                            <TableCell><strong>Topic A</strong></TableCell>
                                            <TableCell><strong>Topic B</strong></TableCell>
                                            <TableCell align="center"><strong>Similarity</strong></TableCell>
                                            <TableCell align="center"><strong>Shared Keywords</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {similarities.map((sim) => (
                                            <TableRow key={sim.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {sim.topic?.title?.substring(0, isMobile ? 30 : 50)}...
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {sim.topic?.university}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {sim.similar_topic?.title?.substring(0, isMobile ? 30 : 50)}...
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                                        {sim.similar_topic?.university}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={`${sim.similarity_score}%`} 
                                                        size="small" 
                                                        color={sim.similarity_color}
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                                                        {sim.shared_keywords?.slice(0, isMobile ? 2 : 3).map((kw, idx) => (
                                                            <Chip key={idx} label={kw} size="small" variant="outlined" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }} />
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {similarities.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">
                                                    <Typography color="text.secondary" sx={{ py: 4 }}>
                                                        No similarity data available. Click "Run Analysis" to generate.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {/* Country Distribution Tab */}
                    {activeTab === 2 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <Grid container spacing={2}>
                                {countryDistribution.map((item, idx) => (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                        <Card sx={{ 
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'translateY(-4px)' }
                                        }}>
                                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: { xs: 1, sm: 0 }
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Flag color="primary" />
                                                        <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                                            {item.country}
                                                        </Typography>
                                                    </Box>
                                                    <Chip 
                                                        label={`${item.percentage}%`} 
                                                        size="small" 
                                                        color="primary"
                                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                    />
                                                </Box>
                                                <Typography variant="h3" sx={{ 
                                                    mt: 2,
                                                    fontSize: { xs: '1.5rem', sm: '2rem' }
                                                }}>
                                                    {item.topic_count}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    Research Topics
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={item.percentage} 
                                                    sx={{ mt: 2, height: 6, borderRadius: 3 }} 
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {countryDistribution.length === 0 && (
                                    <Grid item xs={12}>
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">No country distribution data available</Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}

                    {/* Trending Keywords Tab */}
                    {activeTab === 3 && (
                        <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <Grid container spacing={2}>
                                {trendingKeywords.map((keyword, idx) => (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                        <Card sx={{ 
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'translateY(-4px)' }
                                        }}>
                                            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: { xs: 1, sm: 0 }
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Psychology color="secondary" />
                                                        <Typography variant="subtitle1" sx={{ 
                                                            fontWeight: 'bold',
                                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                                        }}>
                                                            {keyword.keyword}
                                                        </Typography>
                                                    </Box>
                                                    <Chip 
                                                        label={`${keyword.trending_score}%`} 
                                                        size="small" 
                                                        color={keyword.trending_score >= 80 ? 'success' : keyword.trending_score >= 50 ? 'warning' : 'info'}
                                                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                                    />
                                                </Box>
                                                <Typography variant="h4" sx={{ 
                                                    mt: 2,
                                                    fontSize: { xs: '1.5rem', sm: '2rem' }
                                                }}>
                                                    {keyword.frequency}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                    Occurrences
                                                </Typography>
                                                <LinearProgress 
                                                    variant="determinate" 
                                                    value={keyword.trending_score} 
                                                    sx={{ mt: 2, height: 6, borderRadius: 3 }} 
                                                    color={keyword.trending_score >= 80 ? 'success' : keyword.trending_score >= 50 ? 'warning' : 'info'}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {trendingKeywords.length === 0 && (
                                    <Grid item xs={12}>
                                        <Box sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">No trending keywords available</Typography>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </Paper>

                {/* Run Analysis Dialog */}
                <Dialog 
                    open={openAnalysisDialog} 
                    onClose={() => setOpenAnalysisDialog(false)} 
                    maxWidth="sm" 
                    fullWidth
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Run Topic Similarity Analysis
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Typography gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Similarity Threshold: {analysisConfig.threshold}%
                            </Typography>
                            <Slider 
                                value={analysisConfig.threshold} 
                                onChange={(e, v) => setAnalysisConfig({ ...analysisConfig, threshold: v })} 
                                min={0} 
                                max={100} 
                                step={5} 
                                valueLabelDisplay="auto" 
                                sx={{ mb: 3 }} 
                            />
                            <TextField 
                                fullWidth 
                                label="Algorithm Version" 
                                value={analysisConfig.algorithm_version} 
                                onChange={(e) => setAnalysisConfig({ ...analysisConfig, algorithm_version: e.target.value })} 
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                            />
                            <Alert severity="info">
                                This will analyze all research topics and identify similar pairs. The process may take a few minutes.
                            </Alert>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button onClick={() => setOpenAnalysisDialog(false)} size={isMobile ? "small" : "medium"}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleRunAnalysis} 
                            variant="contained" 
                            disabled={runningAnalysis} 
                            startIcon={runningAnalysis ? <CircularProgress size={20} /> : <PlayArrow />}
                            size={isMobile ? "small" : "medium"}
                        >
                            {runningAnalysis ? 'Running...' : 'Run Analysis'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default TopicSimilarityAnalysis;